import { useState } from 'react';
import { useEscape } from "../hooks/useEscape.js";

const CREATED_BY_KEY = 'label_designer_created_by';

interface Props {
  templateLabel: string;
  widthMm: number;
  heightMm: number;
  onConfirm: (r: { widthMm: number; heightMm: number; mode: 'keep' | 'scale'; createdBy: string }) => void;
  onCancel: () => void;
}

/** New label size: keep the elements where they are (mm) or scale them with the label. Saved as a new version. */
export function ResizeTemplateDialog({ templateLabel, widthMm, heightMm, onConfirm, onCancel }: Props) {
  useEscape(onCancel);
  const [w, setW] = useState(String(widthMm));
  const [h, setH] = useState(String(heightMm));
  const [mode, setMode] = useState<'keep' | 'scale'>('scale');
  const [createdBy, setCreatedBy] = useState(localStorage.getItem(CREATED_BY_KEY) ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleConfirm() {
    const width = Number(w.replace(',', '.'));
    const height = Number(h.replace(',', '.'));
    if (!(width > 0 && height > 0)) return setError('Width and height must be positive numbers');
    if (!createdBy.trim()) return setError('Name/email is required');
    localStorage.setItem(CREATED_BY_KEY, createdBy.trim());
    onConfirm({ widthMm: width, heightMm: height, mode, createdBy: createdBy.trim() });
  }

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <h3>Resize label</h3>
        <p className="dialog__subtitle">
          <strong>{templateLabel}</strong>: now {widthMm} × {heightMm} mm. Saved as a new version (the old one stays in the history).
        </p>
        <div className="form-group">
          <label>Width × height (mm)</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="text" value={w} onChange={(e) => setW(e.target.value)} style={{ width: '6rem' }} autoFocus />
            ×
            <input type="text" value={h} onChange={(e) => setH(e.target.value)} style={{ width: '6rem' }} />
          </div>
        </div>
        <div className="form-group">
          <label>Elements</label>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="radio" checked={mode === 'scale'} onChange={() => setMode('scale')} /> Scale with the label (positions, sizes and font sizes)
          </label>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="radio" checked={mode === 'keep'} onChange={() => setMode('keep')} /> Keep where they are (in mm)
          </label>
        </div>
        <div className="form-group">
          <label>Your name / email *</label>
          <input type="text" value={createdBy} onChange={(e) => setCreatedBy(e.target.value)} placeholder="e.g. user@example.com" />
        </div>
        {error && <span className="form-error">{error}</span>}
        <div className="dialog__actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary" onClick={handleConfirm}>Resize</button>
        </div>
      </div>
    </div>
  );
}
